import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'
import { Text } from '@/components/ui/Text'

interface IconLogoPickerProps {
  iconLogoUrl: string | null
  onIconFileChange: (file: File) => void
  disabled?: boolean
}

export function IconLogoPicker({ iconLogoUrl, onIconFileChange, disabled }: IconLogoPickerProps) {
  const { t } = useTranslation('app')
  const inputRef = useRef<HTMLInputElement>(null)

  function handleClick() {
    if (disabled) return
    inputRef.current?.click()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) onIconFileChange(file)
    e.target.value = ''
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    if (disabled) return
    const file = e.dataTransfer.files?.[0]
    if (file) onIconFileChange(file)
  }

  return (
    <button
      type="button"
      data-testid="icon-logo-picker"
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      disabled={disabled}
      className={cn(
        'flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled
          ? 'pointer-events-none opacity-60'
          : 'border-border bg-card-inset dark:bg-card hover:border-muted-foreground/40',
      )}
    >
      {iconLogoUrl ? (
        <img
          src={iconLogoUrl}
          alt="Icon logo"
          className="max-h-12 max-w-[120px] object-contain"
        />
      ) : (
        <Upload className="h-6 w-6 text-muted-foreground" />
      )}
      <Text size="xs" color="muted">{t('brand.icon')}</Text>

      <input
        ref={inputRef}
        type="file"
        accept=".svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp"
        onChange={handleChange}
        className="hidden"
      />
    </button>
  )
}
